import type {
  DeclarativeFileLayout,
  DeclarativeLayoutField,
  FileLayoutDataType,
  ResolvedFileMapping,
} from '@/modules/file-reader/fileReaderTypes'
import { findMappingTargetBySemanticField } from '@/modules/file-reader/mappingTargets'

interface IndexedXmlValue {
  path: string
  value: string
  owner: Element
}

export interface ExecutedLayoutGroup {
  key: string
  label: string
  repeat: boolean
  parent: string | null
  records: Array<Record<string, unknown>>
  recordMetadata?: Array<{
    sourceLine: number
    parentGroup: string | null
    parentSourceLine: number | null
  }>
}

export interface ExecutedFileLayout {
  format: DeclarativeFileLayout['format']
  encoding: string
  sourcePreview: string
  groups: ExecutedLayoutGroup[]
  warnings: string[]
  recordCount: number
}

const MAX_SOURCE_PREVIEW = 150_000
const MAX_RECORDS = 10_000

function encodingAlias(encoding: string) {
  const normalized = encoding.trim().toLowerCase()
  const aliases: Record<string, string> = {
    'iso-8859-1': 'windows-1252',
    latin1: 'windows-1252',
    'utf-16': 'utf-16le',
  }
  return aliases[normalized] || normalized || 'utf-8'
}

function decode(buffer: ArrayBuffer, requestedEncoding?: string) {
  const bytes = new Uint8Array(buffer)
  const bomEncoding = bytes[0] === 0xff && bytes[1] === 0xfe
    ? 'utf-16le'
    : bytes[0] === 0xfe && bytes[1] === 0xff
      ? 'utf-16be'
      : null
  const declaration = new TextDecoder('ascii').decode(bytes.subarray(0, Math.min(512, bytes.length)))
  const xmlEncoding = declaration.match(/<\?xml[^>]*encoding\s*=\s*["']([^"']+)["']/i)?.[1]
  const encoding = encodingAlias(bomEncoding || xmlEncoding || requestedEncoding || 'utf-8')
  try {
    return { encoding, text: new TextDecoder(encoding, { fatal: false }).decode(bytes) }
  } catch {
    return { encoding: 'utf-8', text: new TextDecoder('utf-8', { fatal: false }).decode(bytes) }
  }
}

function stableSegment(localName: string, namespaceUri: string | null, rootNamespaceUri: string | null) {
  return namespaceUri && namespaceUri !== rootNamespaceUri
    ? `{${encodeURIComponent(namespaceUri)}}${localName}`
    : localName
}

function directText(element: Element) {
  return Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE || node.nodeType === Node.CDATA_SECTION_NODE)
    .map((node) => node.textContent || '').join(' ').replace(/\s+/g, ' ').trim()
}

function buildXmlIndex(xml: XMLDocument) {
  const values: IndexedXmlValue[] = []
  const elements = new Map<string, Element[]>()
  const rootNamespaceUri = xml.documentElement.namespaceURI || null
  const visit = (element: Element, parentPath: string) => {
    const path = `${parentPath}/${stableSegment(element.localName, element.namespaceURI, rootNamespaceUri)}`
    elements.set(path, [...(elements.get(path) || []), element])
    Array.from(element.attributes).filter((attribute) => !attribute.name.startsWith('xmlns')).forEach((attribute) => {
      const segment = attribute.namespaceURI
        ? `{${encodeURIComponent(attribute.namespaceURI)}}${attribute.localName}`
        : attribute.localName
      values.push({ path: `${path}/@${segment}`, value: attribute.value, owner: element })
    })
    if (element.children.length === 0) values.push({ path, value: directText(element), owner: element })
    Array.from(element.children).forEach((child) => visit(child, path))
  }
  visit(xml.documentElement, '')
  return { values, elements }
}

function pathMatches(actualPath: string, configuredPath: string) {
  return configuredPath.split('||').some((candidatePath) => {
    const candidate = candidatePath.trim()
    if (!candidate.includes('*')) return actualPath === candidate
    const expression = candidate
      .split('*')
      .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('[^/]+')
    return new RegExp(`^${expression}$`).test(actualPath)
  })
}

function elementsForPath(elements: Map<string, Element[]>, configuredPath: string) {
  return Array.from(elements.entries())
    .filter(([actualPath]) => pathMatches(actualPath, configuredPath))
    .flatMap(([, matches]) => matches)
}

function transformValue(value: string, field: DeclarativeLayoutField) {
  const operation = typeof field.transform?.operation === 'string' ? field.transform.operation : 'trim'
  if (operation === 'uppercase') return value.trim().toUpperCase()
  if (operation === 'lowercase') return value.trim().toLowerCase()
  if (operation === 'digits') return value.replace(/\D/g, '')
  return value.trim()
}

function coerceDate(value: string, dateFormats: string[]) {
  const trimmed = value.trim()
  if (/^\d{8}$/.test(trimmed) && dateFormats.includes('ddMMyyyy')) {
    return `${trimmed.slice(4, 8)}-${trimmed.slice(2, 4)}-${trimmed.slice(0, 2)}`
  }
  if (/^\d{8}$/.test(trimmed) && dateFormats.includes('yyyyMMdd')) {
    return `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`
  }
  return trimmed.slice(0, 10)
}

function coerce(value: string, dataType: FileLayoutDataType, decimalSeparator = '.', dateFormats: string[] = []) {
  if (dataType === 'string') return value
  if (dataType === 'boolean') return ['1', 'true', 'sim', 'yes', 's'].includes(value.trim().toLowerCase())
  if (dataType === 'date') return coerceDate(value, dateFormats)
  const normalized = decimalSeparator === ','
    ? value.replace(/\./g, '').replace(',', '.')
    : value.replace(/,/g, '')
  const number = dataType === 'integer' ? Number.parseInt(normalized, 10) : Number(normalized)
  return Number.isFinite(number) ? number : value
}

function valueForField(
  raw: string | undefined,
  field: DeclarativeLayoutField,
  decimalSeparator?: string,
  dateFormats?: string[],
) {
  const value = raw === undefined || raw === '' ? field.defaultValue : raw
  if (value === undefined || value === null) return undefined
  if (typeof value !== 'string') return value
  return coerce(transformValue(value, field), field.dataType, decimalSeparator, dateFormats)
}

function executeXml(text: string, definition: DeclarativeFileLayout): ExecutedLayoutGroup[] {
  if (/<!DOCTYPE/i.test(text.slice(0, 4096))) throw new Error('xml_doctype_not_allowed')
  const xml = new DOMParser().parseFromString(text, 'application/xml')
  if (xml.querySelector('parsererror') || !xml.documentElement) throw new Error('invalid_xml')
  const index = buildXmlIndex(xml)
  return definition.groups.map((group) => {
    const matchingScopes = elementsForPath(index.elements, group.externalPath)
    const scopes = group.repeat
      ? matchingScopes
      : [matchingScopes[0] || xml.documentElement]
    return {
      key: group.key,
      label: group.label,
      repeat: group.repeat,
      parent: group.parent || null,
      records: scopes.slice(0, MAX_RECORDS).map((scope) => {
        const record: Record<string, unknown> = {}
        group.fields.forEach((field) => {
          const match = index.values.find((candidate) => pathMatches(candidate.path, field.externalPath)
            && (candidate.owner === scope || scope.contains(candidate.owner)))
          const value = valueForField(
            match?.value,
            field,
            definition.parser.decimalSeparator,
            definition.parser.dateFormats,
          )
          if (value !== undefined) record[field.internalField] = value
        })
        return record
      }),
    }
  })
}

function splitDelimitedLine(line: string, delimiter: string, quote: string) {
  if (!quote || !line.includes(quote)) return line.split(delimiter)
  const fields: string[] = []
  let current = ''
  let quoted = false
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index]
    if (character === quote) {
      if (quoted && line[index + 1] === quote) { current += quote; index += 1 } else quoted = !quoted
    } else if (character === delimiter && !quoted) {
      fields.push(current)
      current = ''
    } else current += character
  }
  fields.push(current)
  return fields
}

function normalizedDelimitedFields(line: string, delimiter: string, quote: string) {
  const fields = splitDelimitedLine(line, delimiter, quote)
  if (fields[0] === '') fields.shift()
  if (fields[fields.length - 1] === '') fields.pop()
  return fields
}

function executeDelimited(text: string, definition: DeclarativeFileLayout): ExecutedLayoutGroup[] {
  const delimiter = definition.parser.delimiter || '|'
  const quote = definition.parser.quote || '"'
  const lines = text.split(/\r?\n/).filter((line) => line.trim())
  const indexed = lines.map((line, lineIndex) => ({
    line,
    sourceLine: lineIndex + 1,
    fields: normalizedDelimitedFields(line, delimiter, quote),
  }))
  return definition.groups.map((group) => {
    const matches = indexed.filter((record) => group.externalPath === '*' || record.fields[0] === group.externalPath)
    const selected = (group.repeat ? matches : matches.slice(0, 1)).slice(0, MAX_RECORDS)
    const parentPath = definition.groups.find((candidate) => candidate.key === group.parent)?.externalPath
    let lastParentSourceLine: number | null = null
    const parentBySourceLine = new Map<number, number | null>()
    indexed.forEach((record) => {
      if (parentPath && record.fields[0] === parentPath) lastParentSourceLine = record.sourceLine
      parentBySourceLine.set(record.sourceLine, lastParentSourceLine)
    })
    return {
      key: group.key,
      label: group.label,
      repeat: group.repeat,
      parent: group.parent || null,
      recordMetadata: selected.map((record) => ({
        sourceLine: record.sourceLine,
        parentGroup: group.parent || null,
        parentSourceLine: group.parent ? parentBySourceLine.get(record.sourceLine) || null : null,
      })),
      records: selected.map(({ fields }) => {
        const record: Record<string, unknown> = {}
        group.fields.forEach((field) => {
          const position = /^(?:field|column):(\d+)$/i.exec(field.externalPath)?.[1]
          const raw = position ? fields[Number(position) - 1] : undefined
          const value = valueForField(raw, field, definition.parser.decimalSeparator, definition.parser.dateFormats)
          if (value !== undefined) record[field.internalField] = value
        })
        return record
      }),
    }
  })
}

function executeFixedWidth(text: string, definition: DeclarativeFileLayout): ExecutedLayoutGroup[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim())
  return definition.groups.map((group) => {
    const matches = lines.filter((line) => group.externalPath === '*' || line.startsWith(group.externalPath))
    const selected = (group.repeat ? matches : matches.slice(0, 1)).slice(0, MAX_RECORDS)
    return {
      key: group.key,
      label: group.label,
      repeat: group.repeat,
      parent: group.parent || null,
      records: selected.map((line) => {
        const record: Record<string, unknown> = {}
        group.fields.forEach((field) => {
          const range = /^chars:(\d+)-(\d+)$/i.exec(field.externalPath)
          const raw = range ? line.slice(Number(range[1]) - 1, Number(range[2])) : undefined
          const value = valueForField(raw, field, definition.parser.decimalSeparator, definition.parser.dateFormats)
          if (value !== undefined) record[field.internalField] = value
        })
        return record
      }),
    }
  })
}

export function executeFileLayout(buffer: ArrayBuffer, definition: DeclarativeFileLayout): ExecutedFileLayout {
  const { encoding, text } = decode(buffer, definition.parser.encoding)
  const groups = definition.format === 'xml'
    ? executeXml(text, definition)
    : definition.format === 'delimited_text'
      ? executeDelimited(text, definition)
      : executeFixedWidth(text, definition)
  const warnings: string[] = []
  groups.forEach((group) => {
    const groupDefinition = definition.groups.find((candidate) => candidate.key === group.key)
    const groupRequired = groupDefinition?.required ?? definition.format === 'xml'
    if (group.records.length === 0 && groupRequired) warnings.push(`missing-group:${group.key}`)
    const configuredFields = groupDefinition?.fields || []
    configuredFields.filter((field) => field.required).forEach((field) => {
      if (group.records.some((record) => record[field.internalField] === undefined)) warnings.push(`missing-required:${group.key}.${field.key}`)
    })
  })
  definition.groups.forEach((groupDefinition) => {
    const group = groups.find((candidate) => candidate.key === groupDefinition.key)
    if (!group) return
    groupDefinition.fields.filter((field) => field.reference).forEach((field) => {
      const reference = field.reference
      if (!reference) return
      const referenceGroupDefinition = definition.groups.find((candidate) => candidate.key === reference.group)
      const referenceField = referenceGroupDefinition?.fields.find((candidate) => candidate.key === reference.field)
      const referenceGroup = groups.find((candidate) => candidate.key === reference.group)
      if (!referenceField || !referenceGroup) return
      const knownValues = new Set(referenceGroup.records
        .map((record) => record[referenceField.internalField])
        .filter((value) => value !== undefined && value !== null && value !== '')
        .map(String))
      const unresolvedCount = group.records.filter((record) => {
        const value = record[field.internalField]
        return value !== undefined && value !== null && value !== '' && !knownValues.has(String(value))
      }).length
      if (unresolvedCount > 0) warnings.push(`unresolved-reference:${groupDefinition.key}.${field.key}:${unresolvedCount}`)
    })
  })
  return {
    format: definition.format,
    encoding,
    sourcePreview: text.slice(0, MAX_SOURCE_PREVIEW),
    groups,
    warnings,
    recordCount: groups.reduce((total, group) => total + group.records.length, 0),
  }
}

function setTarget(target: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split('.')
  let cursor = target
  parts.forEach((part, index) => {
    if (index === parts.length - 1) cursor[part] = value
    else {
      const next = cursor[part]
      if (!next || typeof next !== 'object' || Array.isArray(next)) cursor[part] = {}
      cursor = cursor[part] as Record<string, unknown>
    }
  })
}

export function resolveExecutedFileLayout(
  definition: DeclarativeFileLayout,
  execution: ExecutedFileLayout,
): ResolvedFileMapping {
  const movement: Record<string, unknown> = {}
  const movementItems: Array<Record<string, unknown>> = []

  execution.groups.forEach((executedGroup) => {
    const group = definition.groups.find((candidate) => candidate.key === executedGroup.key)
    if (!group) return

    executedGroup.records.forEach((record) => {
      const item: Record<string, unknown> = {}
      group.fields.forEach((field) => {
        const value = record[field.internalField]
        if (value === undefined) return
        const target = findMappingTargetBySemanticField(field.internalField)
        if (!target) return
        setTarget(target.entity === 'movement' ? movement : item, target.field, value)
      })
      if (Object.keys(item).length > 0) movementItems.push(item)
    })
  })

  return { movement, movementItems, warnings: [...execution.warnings] }
}
